import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography, Select, MenuItem } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import { ReplayEngine } from '../../services/replay/ReplayEngine';

interface Props {
  engine: ReplayEngine;
  startOffset?: number;
}

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const ReplayControls: React.FC<Props> = ({ engine, startOffset = 0 }) => {
  // isPlaying drives the button icon — changes are infrequent (user clicks only).
  const [isPlaying, setIsPlaying] = useState(engine.isPlaying);
  const [idle, setIdle] = useState(false);

  // Direct DOM refs for the scrubber and time display.
  // Updated every rAF frame without triggering React re-renders.
  const rangeRef = useRef<HTMLInputElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const rafRef = useRef<number>();
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const resetIdle = useCallback(() => {
    setIdle(false);
    clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIdle(true), 3000);
  }, []);

  // Smooth scrubber: write directly to the DOM, never through React state.
  useEffect(() => {
    const tick = () => {
      const t = engine.currentTime;
      const d = engine.duration;
      if (rangeRef.current) rangeRef.current.value = String(t);
      if (timeRef.current) timeRef.current.textContent = `${formatTime(t)} / ${formatTime(d)}`;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current); };
  }, [engine]);

  // isPlaying: subscribe via onStateChange (fires only when user clicks or engine ends).
  useEffect(() => {
    return engine.onStateChange(() => setIsPlaying(engine.isPlaying));
  }, [engine]);

  // Auto-hide controls after 3s of inactivity.
  useEffect(() => {
    resetIdle();
    window.addEventListener('mousemove', resetIdle);
    window.addEventListener('touchstart', resetIdle);
    return () => {
      clearTimeout(idleTimerRef.current);
      window.removeEventListener('mousemove', resetIdle);
      window.removeEventListener('touchstart', resetIdle);
    };
  }, [resetIdle]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.code === 'Space' || e.code === 'ArrowLeft' || e.code === 'ArrowRight') resetIdle();
    if (e.code === 'Space') { e.preventDefault(); engine.isPlaying ? engine.pause() : engine.play(); }
    if (e.code === 'ArrowLeft') engine.seek(Math.max(startOffset, engine.currentTime - 5000));
    if (e.code === 'ArrowRight') engine.seek(engine.currentTime + 5000);
  }, [engine, startOffset, resetIdle]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'transparent',
        borderTop: 0,
        px: 3,
        py: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        zIndex: 1300,
        opacity: idle ? 0 : 1,
        transition: 'opacity 0.3s ease',
        pointerEvents: idle ? 'none' : 'auto',
      }}
    >
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton size="small" onClick={() => engine.seek(Math.max(startOffset, engine.currentTime - 5000))}>
          <FastRewindIcon fontSize="small" />
        </IconButton>
        <IconButton onClick={() => isPlaying ? engine.pause() : engine.play()}>
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
        <IconButton size="small" onClick={() => { engine.pause(); engine.seek(startOffset); }}>
          <StopIcon fontSize="small" />
        </IconButton>
        <Select
          size="small"
          value={engine.speed}
          onChange={(e) => engine.setSpeed(e.target.value as 1 | 2 | 4)}
          sx={{ ml: 1, minWidth: 64 }}
        >
          <MenuItem value={1}>1×</MenuItem>
          <MenuItem value={2}>2×</MenuItem>
          <MenuItem value={4}>4×</MenuItem>
        </Select>
        <Typography component="span" variant="caption" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
          <span ref={timeRef}>{formatTime(0)} / {formatTime(engine.duration)}</span>
        </Typography>
      </Box>
      <Box sx={{ py: 0.5 }}>
        <input
          ref={rangeRef}
          type="range"
          min={startOffset}
          max={engine.duration}
          defaultValue={engine.currentTime}
          onChange={(e) => engine.seek(Number(e.target.value))}
          style={{ width: '100%', cursor: 'pointer', accentColor: 'currentcolor' }}
        />
      </Box>
    </Box>
  );
};
